import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSite } from '../context/SiteContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import styles from './BlogPost.module.css';

const CATEGORY_LABELS = {
  'trip-report': 'Trip Report',
  'tips':        'Tips & Guides',
  'news':        'News',
};

const CATEGORY_COLORS = {
  'trip-report': { color: '#2d7a3e', bg: '#e6f4ea' },
  'tips':        { color: '#1a56c4', bg: '#e8f0fe' },
  'news':        { color: '#b45309', bg: '#fef3c7' },
};

export default function BlogPost() {
  const { slug } = useParams();
  const { data } = useSite();
  const navigate = useNavigate();

  const post = (data.posts || []).find(p => p.slug === slug && p.published);

  if (!post) {
    return (
      <>
        <SEO
          title="Post Not Found | Keepers Blog"
          description="The blog post you're looking for is not available. Read other hiking tips and trail reviews."
          noindex={true}
        />
        <div className={styles.page}>
          <Navbar />
          <div className={styles.notFound}>
            <h2>Post not found</h2>
            <button className={styles.backBtn} onClick={() => navigate('/blog')}>← Back to Blog</button>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const cat = CATEGORY_COLORS[post.category] || { color: '#555', bg: '#f0f0f0' };

  return (
    <>
      <SEO
        title={`${post.title} | Keepers Blog`}
        description={post.excerpt}
        image={post.coverImage}
        url={`https://keepersexperience.com/blog/${post.slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt,
          "image": post.coverImage,
          "datePublished": post.date,
          "author": {
            "@type": "Person",
            "name": post.author
          },
          "publisher": {
            "@type": "Organization",
            "name": "Keepers Experience",
            "logo": {
              "@type": "ImageObject",
              "url": "https://keepersexperience.com/full_logo.png"
            }
          }
        }}
      />
      <div className={styles.page}>
      <Navbar />

      {/* Cover */}
      <div className={styles.cover}>
        <img src={post.coverImage} alt={post.title} />
        <div className={styles.coverOverlay} />
      </div>

      <div className={styles.container}>
        <motion.div
          className={styles.articleHeader}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className={styles.meta}>
            <span className={styles.catBadge} style={{ color: cat.color, background: cat.bg }}>
              {CATEGORY_LABELS[post.category] || post.category}
            </span>
            <span className={styles.metaDivider}>·</span>
            <span className={styles.metaDate}>{post.date}</span>
            <span className={styles.metaDivider}>·</span>
            <span className={styles.metaAuthor}>{post.author}</span>
          </div>
          <h1 className={styles.title}>{post.title}</h1>
          <p className={styles.excerpt}>{post.excerpt}</p>
        </motion.div>

        <motion.div
          className={styles.content}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          {post.sections.map(section => (
            <div key={section.id} className={styles.section}>
              {section.heading && <h2 className={styles.sectionHeading}>{section.heading}</h2>}
              <p className={styles.sectionBody}>{section.body}</p>
            </div>
          ))}
        </motion.div>

        <button className={styles.backBtn} onClick={() => navigate('/blog')}>
          ← Back to Blog
        </button>
      </div>

      <Footer />
    </div>
    </>
  );
}
